import React, { useContext, useEffect, useState } from 'react'
import { DocusealForm } from '@docuseal/react'
import { UserContext } from 'components/context'
import PropTypes from 'prop-types'
// import { useLogEvent } from 'components/hooks'

// The member's own submission when memberships created one (so DocuSeal can
// remind them), else the template form, which creates a submission on opening
const signingForm = (signing, membership, doc) => {
  if (signing.slugType === 'submitter') return { src: `https://docuseal.eu/s/${signing.slug}` }

  return {
    token: signing.token,
    src: `https://docuseal.eu/d/${doc.template_slug}`,
    email: membership.profile.email,
    name: membership.profile.name,
    externalId: membership.id,
  }
}

const DocumentSignModal = ({ membership, doc, onClose }) => {
  // { token, slug, slugType }: slugType 'submitter' is the member's own DocuSeal
  // submission, created by memberships; otherwise the template form is signed
  // with the token
  const [signing, setSigning] = useState(null)
  const { get, post, logEvent } = useContext(UserContext)

  useEffect(() => {
    if (signing) return

    // The response body is { token, slug, slug_type } as memberships returns it
    get(`/v1/membership/documents/${doc.document_id}/token`)
      .then((res) => {
        if (res?.slug || res?.token) {
          setSigning({ token: res.token, slug: res.slug, slugType: res.slug_type })
        } else {
          console.error('Token not available', res)
        }
      })
      .catch((err) => console.error('fetch token error', err))
  }, [doc, get, membership, signing])

  // The theme's variables (--primary etc.) are set on the .theme element, not the root
  const themeEl = window.document.querySelector('.theme') || window.document.documentElement
  const styles = getComputedStyle(themeEl)

  const theme = {
    primary: styles.getPropertyValue('--primary').trim(),
    secondary: styles.getPropertyValue('--secondary').trim(),
    accent1: styles.getPropertyValue('--accent1').trim(),
    accent2: styles.getPropertyValue('--accent2').trim(),
    accent3: styles.getPropertyValue('--accent3').trim(),
  }

  const customCss = `
    .form-container.p-4 {
      padding: 8px;
    }
    label.field-name-label, .base-input {
      font-size: 14px !important;
      line-height: 18px !important;
    }
    label.field-name-label {
        margin-bottom: 12px;
    }
    .base-input {
      border-radius: 0.5em;
    }
    .submit-form-button {
      background-color: ${theme.primary};
      border-radius: 0.5em;
    }
    .base-button.w-full,
    .base-input.w-full {
      width: 50%;
      margin-left: 25%;
    }
  `

  const handleSigned = async (data) => {
    try {
      const params = {
        id: doc.id,
        operator_id: membership.operator.id,
        membership_id: membership.id,
        document_id: doc.document_id,
        signed: data.status === 'completed',
        data,
      }

      await storeResponse(params)

      logEvent({
        action: 'sign_document',
        status: data.status,
        details: { name: data.template.name },
      }).catch(console.error)

      onClose(data.status === 'completed')
    } catch (err) {
      console.error(err)
    }
  }

  const storeResponse = async (params) => {
    const resp = await new Promise((resolve, reject) => {
      const url = `/v1/membership/documents/${params.document_id}/sign`
      post(url, params)
        .then((data) => {
          resolve(data)
        })
    })
    return resp
  }

  const handleLoad = () => {
    const formEl = document.querySelector('docuseal-form')

    if (!formEl) return

    const shadow = formEl.shadowRoot
    if (!shadow) return

    // add style to hide the form field container
    const style = document.createElement('style')
    style.id = 'scroll-gate'
    style.textContent = `
      .form-container { visibility: hidden !important; }
    `
    shadow.appendChild(style)

    // On a wide screen the docuseal-form element scrolls, on a phone DocuSeal
    // scrolls the document in its own #scrollbox inside the shadow root.
    // Scroll events don't bubble, so listen on the element and, in the capture
    // phase, on the shadow root, and check whichever element scrolled.
    const onScroll = (e) => {
      const el = e.target
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1000

      if (atBottom) {
        // remove style that is hiding the form field container
        const gate = shadow.getElementById('scroll-gate')
        if (gate) gate.remove()
        formEl.removeEventListener('scroll', onScroll)
        shadow.removeEventListener('scroll', onScroll, true)
      }
    }

    formEl.addEventListener('scroll', onScroll)
    shadow.addEventListener('scroll', onScroll, true)
  }

  return (
    <div className='modal-overlay'>
      <div className='modal-content' onClick={(e) => e.stopPropagation()}>
        <div>
          {/* Rendered once the token is in, so it loads the member's own submission */}
          {signing && (
            <DocusealForm
              {...signingForm(signing, membership, doc)}
              autoScroll={false}
              withDecline={false}
              onComplete={handleSigned}
              onDecline={handleSigned}
              customCss={customCss}
              orderAsOnPage={true} /* eslint-disable-line react/jsx-boolean-value */
              goToLast={false}
              values={{ FullName: membership.profile.name }}
              onLoad={handleLoad}
            />
          )}
        </div>
        <div className='modal-center'>
          <button className='off-button' onClick={e => onClose(false)}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

DocumentSignModal.propTypes = {
  doc: PropTypes.object,
  membership: PropTypes.object,
  onClose: PropTypes.func,
}

export default DocumentSignModal
